import { Injectable } from '@angular/core';
import { Scenario } from '../models/scenario.interface';
import ExcelJS from 'exceljs';

@Injectable({
  providedIn: 'root'
})
export class ScenarioExportService {

  // ERPA brand colors
  private readonly BRAND_PRIMARY = '1B3A5C';    // Dark navy blue
  private readonly BRAND_ACCENT = '2E86C1';     // Medium blue
  private readonly HEADER_BG = '1B3A5C';        // Dark navy for headers
  private readonly HEADER_FONT = 'FFFFFF';       // White text on headers
  private readonly INFO_LABEL_BG = 'D6EAF8';    // Light blue for info labels
  private readonly INFO_VALUE_BG = 'FFFFFF';     // White for info values
  private readonly STEP_HEADER_BG = '2E86C1';   // Medium blue for step table header
  private readonly STEP_ALT_BG = 'EBF5FB';      // Very light blue alternating rows
  private readonly BORDER_COLOR = 'B0C4DE';      // Light steel blue borders

  /**
   * Export scenario steps to a formatted Excel file
   * with ERPA branding, logo, colors, and proper step layout
   */
  async exportScenarioStepsToExcel(scenario: Scenario, steps: any[]): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'ERPA Test Accelerator';
    workbook.created = new Date();

    const worksheet = workbook.addWorksheet('Test Scenario', {
      properties: { defaultColWidth: 20 }
    });

    // Set column widths
    worksheet.columns = [
      { width: 10 },  // A - Step #
      { width: 55 },  // B - Step Description
      { width: 25 },  // C - Value
    ];

    let currentRow = 1;

    // --- Logo & Title Section ---
    currentRow = await this.addLogoAndTitle(workbook, worksheet, currentRow);

    // --- Scenario Information Section ---
    currentRow = this.addScenarioInfo(worksheet, scenario, currentRow);

    // --- Steps Table Section ---
    currentRow = this.addStepsTable(worksheet, scenario, steps, currentRow);

    // --- Footer ---
    this.addFooter(worksheet, currentRow);

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${scenario.scenarioName || 'scenario'}_${timestamp}.xlsx`;
    this.downloadBlob(blob, filename);
  }

  private async addLogoAndTitle(
    workbook: ExcelJS.Workbook,
    worksheet: ExcelJS.Worksheet,
    startRow: number
  ): Promise<number> {
    let currentRow = startRow;

    // Logo row (separate from title to avoid overlap)
    try {
      const logoResponse = await fetch('assets/images/erpa-logo-one2.png');
      if (logoResponse.ok) {
        const logoBuffer = await logoResponse.arrayBuffer();
        const logoImage = workbook.addImage({
          buffer: logoBuffer,
          extension: 'png',
        });

        // Place logo in its own row spanning 3 rows height
        worksheet.mergeCells(currentRow, 1, currentRow + 2, 3);
        worksheet.addImage(logoImage, {
          tl: { col: 0.2, row: currentRow - 1 + 0.2 },
          ext: { width: 180, height: 55 }
        });
        const logoCell = worksheet.getCell(currentRow, 1);
        logoCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
        logoCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.getRow(currentRow).height = 20;
        worksheet.getRow(currentRow + 1).height = 20;
        worksheet.getRow(currentRow + 2).height = 20;
        currentRow += 3;
      }
    } catch {
      // Logo not available - continue without it
    }

    // Title row (merged across all columns)
    worksheet.mergeCells(currentRow, 1, currentRow, 3);
    const titleCell = worksheet.getCell(currentRow, 1);
    titleCell.value = 'ERPA Test Accelerator - Test Scenario Report';
    titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: this.HEADER_FONT } };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.HEADER_BG } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getRow(currentRow).height = 40;
    currentRow++;

    // Subtitle row
    worksheet.mergeCells(currentRow, 1, currentRow, 3);
    const subtitleCell = worksheet.getCell(currentRow, 1);
    subtitleCell.value = `Generated on: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
    subtitleCell.font = { name: 'Calibri', size: 10, italic: true, color: { argb: this.BRAND_ACCENT } };
    subtitleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F0F6FC' } };
    subtitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getRow(currentRow).height = 22;
    currentRow++;

    // Spacer
    currentRow++;

    return currentRow;
  }

  private addScenarioInfo(worksheet: ExcelJS.Worksheet, scenario: Scenario, startRow: number): number {
    let currentRow = startRow;

    // Section header
    worksheet.mergeCells(currentRow, 1, currentRow, 3);
    const sectionHeader = worksheet.getCell(currentRow, 1);
    sectionHeader.value = 'Scenario Information';
    sectionHeader.font = { name: 'Calibri', size: 12, bold: true, color: { argb: this.HEADER_FONT } };
    sectionHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.BRAND_ACCENT } };
    sectionHeader.alignment = { vertical: 'middle', horizontal: 'left' };
    worksheet.getRow(currentRow).height = 28;
    currentRow++;

    // Info rows
    const infoRows = [
      { label: 'Package Name', value: this.getPackageName(scenario) },
      { label: 'Functional Area', value: scenario.functionalArea?.name || '-' },
      { label: 'Test Type', value: scenario.testType?.referenceValue || '-' },
      { label: 'Scenario ID', value: scenario.scenarioId || '-' },
      { label: 'Scenario Name', value: scenario.scenarioName || '-' },
      { label: 'Description', value: scenario.scenarioDescription || '-' },
    ];

    for (const info of infoRows) {
      // Label cell (column A)
      const labelCell = worksheet.getCell(currentRow, 1);
      labelCell.value = info.label;
      labelCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: '2C3E50' } };
      labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.INFO_LABEL_BG } };
      labelCell.alignment = { vertical: 'middle', horizontal: 'left' };
      this.setBorder(labelCell);

      // Value cell (columns B-C merged)
      worksheet.mergeCells(currentRow, 2, currentRow, 3);
      const valueCell = worksheet.getCell(currentRow, 2);
      valueCell.value = info.value;
      valueCell.font = { name: 'Calibri', size: 11, color: { argb: '333333' } };
      valueCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.INFO_VALUE_BG } };
      valueCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      this.setBorder(valueCell);

      worksheet.getRow(currentRow).height = 24;
      currentRow++;
    }

    // Spacer
    currentRow++;

    return currentRow;
  }

  private addStepsTable(
    worksheet: ExcelJS.Worksheet,
    scenario: Scenario,
    steps: any[],
    startRow: number
  ): number {
    let currentRow = startRow;

    // Section header
    worksheet.mergeCells(currentRow, 1, currentRow, 3);
    const sectionHeader = worksheet.getCell(currentRow, 1);
    sectionHeader.value = `Test Steps (${steps?.length || 0} steps)`;
    sectionHeader.font = { name: 'Calibri', size: 12, bold: true, color: { argb: this.HEADER_FONT } };
    sectionHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.BRAND_ACCENT } };
    sectionHeader.alignment = { vertical: 'middle', horizontal: 'left' };
    worksheet.getRow(currentRow).height = 28;
    currentRow++;

    // Table headers
    const headers = ['Step #', 'Step Description', 'Value'];
    const headerRow = worksheet.getRow(currentRow);
    headers.forEach((header, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = header;
      cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: this.HEADER_FONT } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.STEP_HEADER_BG } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      this.setBorder(cell);
    });
    headerRow.height = 28;
    currentRow++;

    // Step data rows
    if (steps && steps.length > 0) {
      steps.forEach((step: any, index: number) => {
        const isAlternate = index % 2 === 1;
        const bgColor = isAlternate ? this.STEP_ALT_BG : 'FFFFFF';
        const row = worksheet.getRow(currentRow);

        // Step #
        const stepNumCell = row.getCell(1);
        stepNumCell.value = step.stepId || index + 1;
        stepNumCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: this.BRAND_PRIMARY } };
        stepNumCell.alignment = { vertical: 'middle', horizontal: 'center' };
        stepNumCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        this.setBorder(stepNumCell);

        // Step Description (use placeholder field as the step text)
        const descCell = row.getCell(2);
        descCell.value = step.placeholder || step.stepDescription || '-';
        descCell.font = { name: 'Calibri', size: 11, color: { argb: '333333' } };
        descCell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
        descCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        this.setBorder(descCell);

        // Value (extract from additionalInfo JSON; mask credentials)
        const valueCell = row.getCell(3);
        valueCell.value = this.extractValue(step.additionalInfo, step.placeholder || step.stepDescription || '');
        valueCell.font = { name: 'Calibri', size: 11, color: { argb: '555555' } };
        valueCell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
        valueCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        this.setBorder(valueCell);

        // Auto-height based on description length
        const descLength = (step.placeholder || step.stepDescription || '').length;
        row.height = Math.max(24, Math.min(80, Math.ceil(descLength / 50) * 18));

        currentRow++;
      });
    } else {
      // No steps message
      worksheet.mergeCells(currentRow, 1, currentRow, 3);
      const noStepsCell = worksheet.getCell(currentRow, 1);
      noStepsCell.value = 'No steps available for this scenario';
      noStepsCell.font = { name: 'Calibri', size: 11, italic: true, color: { argb: '888888' } };
      noStepsCell.alignment = { vertical: 'middle', horizontal: 'center' };
      noStepsCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8F9FA' } };
      this.setBorder(noStepsCell);
      worksheet.getRow(currentRow).height = 30;
      currentRow++;
    }

    // Spacer
    currentRow++;

    return currentRow;
  }

  private addFooter(worksheet: ExcelJS.Worksheet, startRow: number): void {
    worksheet.mergeCells(startRow, 1, startRow, 3);
    const footerCell = worksheet.getCell(startRow, 1);
    footerCell.value = 'ERPA Test Accelerator | Confidential';
    footerCell.font = { name: 'Calibri', size: 9, italic: true, color: { argb: '999999' } };
    footerCell.alignment = { vertical: 'middle', horizontal: 'center' };
  }

  /**
   * Get package name from nested scenario structure
   */
  private getPackageName(scenario: Scenario): string {
    if (scenario.functionalArea?.package?.packageName) {
      return scenario.functionalArea.package.packageName;
    }
    if (scenario.functionalArea?.clientPackageMapping?.package?.packageName) {
      return scenario.functionalArea.clientPackageMapping.package.packageName;
    }
    if (scenario.package?.packageName) {
      return scenario.package.packageName;
    }
    return '-';
  }

  /**
   * Extract the 'value' field from additionalInfo JSON string
   * Username/Password steps return a placeholder instead of the real value.
   */
  private extractValue(additionalInfo: string | null | undefined, stepText: string = ''): string {
    if (!additionalInfo) return '';
    try {
      const parsed = JSON.parse(additionalInfo);
      const raw = parsed?.value || '';
      if (this.isCredentialStep(stepText)) {
        return raw ? '<user provided value>' : '';
      }
      return raw;
    } catch {
      return '';
    }
  }

  private isCredentialStep(text: string): boolean {
    const t = (text || '').toLowerCase();
    return /\b(username|user\s*name|user id|userid|email|password|passcode|pwd)\b/.test(t);
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private setBorder(cell: ExcelJS.Cell): void {
    cell.border = {
      top: { style: 'thin', color: { argb: this.BORDER_COLOR } },
      left: { style: 'thin', color: { argb: this.BORDER_COLOR } },
      bottom: { style: 'thin', color: { argb: this.BORDER_COLOR } },
      right: { style: 'thin', color: { argb: this.BORDER_COLOR } },
    };
  }
}
